'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useReducedMotion,
} from 'framer-motion';

import DashboardWidget from './DashboardWidget';

// --- Main Component ---
export default function DashboardShowcase({
  product,
}: {
  product?: any;
}) {
  const sectionRef = useRef(null);
  const containerRef = useRef(null);

  const [isMobile, setIsMobile] = useState(false);
  const [cropH, setCropH] = useState<number | undefined>(undefined);
  const cropRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');

    const updateIsMobile = () => setIsMobile(mediaQuery.matches);

    updateIsMobile();

    mediaQuery.addEventListener('change', updateIsMobile);

    return () =>
      mediaQuery.removeEventListener('change', updateIsMobile);
  }, []);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  useEffect(() => {
    const el = cropRef.current;
    if (!el || !isMobile) return;
    const update = () => setCropH(Math.round(el.scrollHeight * 0.86));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMobile]);

  const springConfig = {
    stiffness: 70,
    damping: 34,
    restDelta: 0.001,
  };

  // Desktop cinematic animation only
  const rotateXRaw = useTransform(
    scrollYProgress,
    [0, 0.5],
    [14, 0]
  );

  const rotateYRaw = useTransform(
    scrollYProgress,
    [0, 0.5],
    [-8, 0]
  );

  const rotateX = useSpring(rotateXRaw, springConfig);

  const rotateY = useSpring(rotateYRaw, springConfig);

  const scale = useSpring(
    useTransform(scrollYProgress, [0, 0.5], [0.95, 1]),
    springConfig
  );

  const opacity = useTransform(
    scrollYProgress,
    [0, 0.12],
    [0, 1]
  );

  return (
    <section
      ref={sectionRef}
      className="relative pt-16 md:pt-20 pb-24 md:pb-32 bg-slate-50 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 [perspective:1200px]">
        {/* Header Section */}
        <div className="text-center mb-12 md:mb-16">

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-2xl md:text-4xl font-[800] text-[#0f172a] leading-tight tracking-[-0.02em] mb-6"
          >
            {product?.id === 'manufacturing-erp' ? (
              <>Your Entire Business, <br /><span className="text-[#2C78B9]">One Unified Dashboard</span></>
            ) : (
              <>{product?.headline || "Your Entire Business,"} <br /><span style={{ color: product?.accentColor || "#2C78B9" }}>One Unified Dashboard</span></>
            )}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="
              mx-auto
              text-slate-500
              font-medium
              leading-relaxed

              max-w-[95%]
              sm:max-w-2xl

              text-sm
              sm:text-base
              md:text-lg
            "
          >
            {product?.dashHeading ||
              'Stop hunting for data across tabs. Mx-ERP brings your manufacturing, sales, and accounts into a single, high-fidelity command center.'}
          </motion.p>
        </div>

        {/* Dashboard Frame */}
        <motion.div
          ref={containerRef}
          style={
            reduceMotion || isMobile
              ? {
                scale: 1,
                opacity: 1,
              }
              : {
                rotateX,
                rotateY,
                scale,
                opacity,
              }
          }
          className="
            relative
            mx-auto
            w-full
            max-w-6xl
            transform-gpu
            will-change-transform

            rounded-[1.8rem]
            sm:rounded-[2.2rem]
            md:rounded-[2.5rem]

            p-[1px]
            sm:p-3
            md:p-4

            bg-gradient-to-br
            from-[#6366f1]
            via-[#a855f7]
            to-[#ec4899]

            shadow-[0_10px_40px_rgba(168,85,247,0.18)]

            sm:shadow-[0_25px_80px_rgba(168,85,247,0.22)]

            md:shadow-[0_50px_100px_-20px_rgba(168,85,247,0.30)]

            overflow-hidden
          "
        >
          {/* Glow */}
          <div
            className="
              absolute
              inset-0
              opacity-40
              blur-3xl
              bg-gradient-to-r
              from-indigo-500
              via-fuchsia-500
              to-pink-500
            "
          />

          {/* Content */}
          <div
            className="
              relative
              z-10

              h-full
              w-full

              overflow-hidden
              bg-white

              rounded-[1.7rem]
              sm:rounded-[1.8rem]

              backdrop-blur-2xl
            "
          >
            {/* MOBILE VIEW */}
            <div className="block md:hidden">
              <div
                className="
                  relative
                  w-full
                  overflow-hidden
                  bg-white
                "
                style={{ height: cropH }}
              >
                <div
                  ref={cropRef}
                  className="
                    origin-top
                    scale-[0.86]
                  "
                  style={{
                    width: '116.3%',
                    marginLeft: '-8.15%',
                  }}
                >
                  <DashboardWidget
                    type={
                      product?.dashboardType === 'trading' ? 'traders' :
                        product?.dashboardType === 'project-management' ? 'construction' :
                          product?.dashboardType === 'ra-billing' ? 'construction' :
                            product?.dashboardType === 'contract-management' ? 'construction' :
                              product?.dashboardType === 'quantity-surveyor' ? 'construction' :
                                product?.dashboardType === 'hospital-erp' ? 'hospital' :
                                  product?.dashboardType === 'lab-erp' ? 'lab' :
                                    product?.dashboardType === 'ris-erp' ? 'ris' :
                                    product?.dashboardType === 'pharmacy-erp' ? 'pharmacy' :
                                      product?.dashboardType === 'blood-bank-erp' ? 'blood-bank' :
                                        product?.dashboardType === 'queue' ? 'queue' :
                                        product?.dashboardType ||
                                        'manufacturing'
                    }
                    accentColor={product?.accentColor}
                  />
                </div>
              </div>
            </div>

            {/* DESKTOP VIEW */}
            <div className="hidden md:block">
              <div className="aspect-[16/10] w-full">
                <DashboardWidget
                  type={
                    product?.dashboardType === 'trading' ? 'traders' :
                      product?.dashboardType === 'project-management' ? 'construction' :
                        product?.dashboardType === 'ra-billing' ? 'construction' :
                          product?.dashboardType === 'contract-management' ? 'construction' :
                            product?.dashboardType === 'quantity-surveyor' ? 'construction' :
                              product?.dashboardType === 'hospital-erp' ? 'hospital' :
                                product?.dashboardType === 'lab-erp' ? 'lab' :
                                  product?.dashboardType === 'ris-erp' ? 'ris' :
                                    product?.dashboardType === 'pharmacy-erp' ? 'pharmacy' :
                                      product?.dashboardType === 'blood-bank-erp' ? 'blood-bank' :
                                        product?.dashboardType === 'queue' ? 'queue' :
                                        product?.dashboardType ||
                                        'manufacturing'
                  }
                  accentColor={product?.accentColor}
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}